/**
 * The memory book in someone's hands, built in the browser: a fixed photo, a cover laid on the
 * book's front board and warped onto it, then the light and the hands over it. The same scene
 * as the app's (Lavendla app `src/features/memoryBook/lifestyle.ts`), so no picture is made per page.
 */

export const SCENE_ASSETS = "/wp-content/assets/images/memory-book";

export const SCENE_SIZE = { height: 1024, width: 1536 } as const;

/** The front board's corners in the photo (TL, TR, BR, BL), the hinge strip included. */
const QUAD: readonly [number, number][] = [
  [284, 302],
  [906, 169],
  [1121, 682],
  [464, 855],
];

/** The flat board before the warp: the hinge strip by the spine, then the square cover. */
export const SCENE_BOARD = { height: 1000, width: 1070 } as const;

/** Where the cropped layers sit in the photo. */
export const SCENE_LAYERS = {
  hands: { height: 530, width: 760, x: 364, y: 328 },
  light: { height: 684, width: 836, x: 284, y: 170 },
} as const;

/** The book's middle in the photo, kept in the middle of the card's crop. */
export const SCENE_FOCUS = { x: 702, y: 512 } as const;

type Matrix3 = [
  [number, number, number],
  [number, number, number],
  [number, number, number],
];

/** The projective map taking src[i] to dst[i] (h33 = 1). */
export function homography(
  src: readonly (readonly [number, number])[],
  dst: readonly (readonly [number, number])[]
): Matrix3 {
  const a: number[][] = [];
  const b: number[] = [];
  src.forEach(([x, y], i) => {
    const [u, v] = dst[i];
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    a.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  });
  for (let c = 0; c < 8; c += 1) {
    let p = c;
    for (let r = c + 1; r < 8; r += 1) {
      if (Math.abs(a[r][c]) > Math.abs(a[p][c])) {
        p = r;
      }
    }
    [a[c], a[p]] = [a[p], a[c]];
    [b[c], b[p]] = [b[p], b[c]];
    for (let r = 0; r < 8; r += 1) {
      if (r !== c) {
        const f = a[r][c] / a[c][c];
        for (let k = c; k < 8; k += 1) {
          a[r][k] -= f * a[c][k];
        }
        b[r] -= f * b[c];
      }
    }
  }
  const h = b.map((value, i) => value / a[i][i]);
  return [
    [h[0], h[1], h[2]],
    [h[3], h[4], h[5]],
    [h[6], h[7], 1],
  ];
}

type Quad = readonly (readonly [number, number])[];

/** A flat sheet of this size warped onto these corners (TL, TR, BR, BL), as a CSS transform. */
export function quadTransform(
  size: { width: number; height: number },
  quad: Quad
): string {
  const { width, height } = size;
  const m = homography(
    [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height],
    ],
    quad
  );
  // CSS matrix3d is column-major; the projective row goes into the w terms.
  const values = [
    m[0][0],
    m[1][0],
    0,
    m[2][0],
    m[0][1],
    m[1][1],
    0,
    m[2][1],
    0,
    0,
    1,
    0,
    m[0][2],
    m[1][2],
    0,
    m[2][2],
  ];
  return `matrix3d(${values.join(",")})`;
}

/** The board laid flat, warped onto the book in the photo, as a CSS transform. */
export function boardTransform(): string {
  return quadTransform(SCENE_BOARD, QUAD);
}

/**
 * A scene scaled to cover a box of this size, zoomed in on its focus and kept inside the photo.
 * Returns the CSS transform for the scene's photo-sized layer (the book's by default).
 */
export function coverFit(
  width: number,
  height: number,
  zoom: number,
  size: { width: number; height: number } = SCENE_SIZE,
  focus: { x: number; y: number } = SCENE_FOCUS
): string {
  const scale = Math.max(width / size.width, height / size.height) * zoom;
  const clamp = (value: number, min: number) =>
    Math.min(0, Math.max(min, value));
  const x = clamp(width / 2 - focus.x * scale, width - size.width * scale);
  const y = clamp(height / 2 - focus.y * scale, height - size.height * scale);
  return `translate(${x}px, ${y}px) scale(${scale})`;
}
